import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Camera lifecycle hook.
 *
 * States:
 *   - "idle": nothing requested yet
 *   - "prompt": waiting for user gesture / native dialog
 *   - "granted": active stream
 *   - "denied": user denied in native dialog (can be retried)
 *   - "blocked": blocked at browser/OS level (needs manual settings)
 *   - "unsupported": browser has no getUserMedia / no camera
 *   - "error": generic failure
 *
 * Facing: "user" (frontal) | "environment" (traseira)
 *
 * MUST be invoked from a user gesture (click/tap) on iOS Safari.
 */
export default function useCamera() {
  const [state, setState] = useState("idle");
  const [facing, setFacing] = useState("user");
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  const [permissionState, setPermissionState] = useState(null); // from Permissions API
  const streamRef = useRef(null);

  const supported = typeof navigator !== "undefined"
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

  // Probe permission state (best-effort; not all browsers expose it)
  const probePermission = useCallback(async () => {
    if (!supported) return null;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const r = await navigator.permissions.query({ name: "camera" });
        setPermissionState(r.state);
        if (r.state === "denied") setState("blocked");
        return r.state;
      }
    } catch {}
    return null;
  }, [supported]);

  useEffect(() => {
    if (!supported) {
      setState("unsupported");
      return;
    }
    probePermission();
  }, [supported, probePermission]);

  const stop = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => {
        try { t.stop(); } catch {}
      });
    }
    streamRef.current = null;
    setStream(null);
  }, []);

  const start = useCallback(async (nextFacing) => {
    if (!supported) {
      setState("unsupported");
      return null;
    }
    stop();
    setState("prompt");
    setError("");
    const useFacing = nextFacing || facing;
    const constraints = {
      video: {
        facingMode: { ideal: useFacing },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    };
    try {
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      setStream(s);
      setFacing(useFacing);
      setState("granted");
      setPermissionState("granted");
      return s;
    } catch (err) {
      const name = err && err.name;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setState(permissionState === "denied" ? "blocked" : "denied");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setState("unsupported");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setState("error");
        setError("A câmera está em uso por outro aplicativo.");
      } else {
        setState("error");
        setError(err?.message || "Falha ao acessar a câmera.");
      }
      return null;
    }
  }, [supported, facing, permissionState, stop]);

  const flip = useCallback(async () => {
    const next = facing === "user" ? "environment" : "user";
    await start(next);
  }, [facing, start]);

  // Pause/resume on visibility change (iOS Safari freezes streams when backgrounded)
  useEffect(() => {
    if (!stream) return;
    const onVisibility = () => {
      if (document.hidden) {
        // keep stream but it will likely freeze — we just note it
      } else {
        // re-prime by re-attaching to video element happens in consumer
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => stop, [stop]);

  return {
    supported, state, stream, facing, error,
    permissionState,
    start, stop, flip,
  };
}
